/**
 * Abonneer-links per agenda-aanbieder.
 *
 * Er bestaat geen API — niet bij Apple, niet bij Google — waarmee een app een gekozen agenda op een
 * ICS-URL kan abonneren. Wat wél kan is de URL aan de aanbieder overhandigen in de vorm die díe
 * begrijpt, waarna hun eigen scherm de accountkeuze afhandelt. Dat scheelt de medewerker het hele
 * kopiëren-plakken-ritueel.
 */

export type SubscribeProvider = "apple" | "google" | "outlook-personal" | "outlook-work";

export interface SubscribeLink {
  provider: SubscribeProvider;
  url: string;
  /**
   * `webcal` moet door het besturingssysteem geopend worden (een gewone `<a href>`), niet via
   * `openExternalUrl()`: die weigert bewust alles wat geen http(s) is, en de Capacitor Browser
   * (SFSafariViewController / Custom Tabs) kan het schema sowieso niet aan.
   */
  scheme: "webcal" | "https";
}

/**
 * Alleen een absolute http(s)-URL kan een abonnement worden. `VITE_BACKEND_URL` kan in theorie leeg
 * zijn, en dan is de "feed-URL" een relatief pad waar geen enkele agenda iets mee kan.
 */
function isAbsoluteHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === "https:" || protocol === "http:";
  } catch {
    return false;
  }
}

/** Hetzelfde adres, maar met het schema dat agenda-apps als "abonneren" herkennen. */
export function toWebcalUrl(feedUrl: string): string {
  return feedUrl.replace(/^https?:\/\//i, "webcal://");
}

export function buildSubscribeLinks(options: {
  feedUrl: string;
  /** Naam die de aanbieder aan de agenda geeft. Alleen Outlook accepteert er een. */
  calendarName: string;
  platform: string;
}): SubscribeLink[] {
  const { feedUrl, calendarName, platform } = options;

  if (!isAbsoluteHttpUrl(feedUrl)) {
    return [];
  }

  const webcalUrl = toWebcalUrl(feedUrl);
  const name = encodeURIComponent(calendarName);

  const apple: SubscribeLink = {
    provider: "apple",
    url: webcalUrl,
    scheme: "webcal",
  };

  // Google's abonneer-scherm wil het webcal-adres in `cid`; met https opent het een leeg formulier.
  const google: SubscribeLink = {
    provider: "google",
    url: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcalUrl)}`,
    scheme: "https",
  };

  // Outlook haalt de feed server-side op en wil daarom juist het https-adres.
  const outlookPersonal: SubscribeLink = {
    provider: "outlook-personal",
    url: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl)}&name=${name}`,
    scheme: "https",
  };

  const outlookWork: SubscribeLink = {
    provider: "outlook-work",
    url: `https://outlook.office.com/calendar/0/addfromweb?url=${encodeURIComponent(feedUrl)}&name=${name}`,
    scheme: "https",
  };

  // Android heeft geen handler voor `webcal:` — een Apple-knop doet daar niets en zou dus liegen.
  if (platform === "android") {
    return [google, outlookPersonal, outlookWork];
  }

  if (platform === "ios") {
    return [apple, google, outlookPersonal, outlookWork];
  }

  return [apple, google, outlookPersonal, outlookWork];
}

/**
 * Op Android kun je nergens in de Google Agenda-app een agenda via URL toevoegen; dat bestaat
 * alleen op calendar.google.com op een computer. De knop hierboven brengt je daar wel, maar op een
 * telefoon loopt dat dood — dus zeggen we het, in plaats van iemand het te laten ontdekken.
 */
export function needsDesktopHint(platform: string): boolean {
  return platform === "android";
}
